import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FileText, Loader2, RefreshCw } from 'lucide-react';
import { apiService } from '../../../services/api';
import {
  formatDate,
  formatMoney,
  invoiceStatusLabel,
  statusClass,
} from '../../BillingBatchManagement/billingMath';

const todayIso = () => new Date().toISOString().slice(0, 10);

const normalizeAnchor = (anchor) =>
  anchor === 'iteration_start' ? 'iteration_start' : 'iteration_end';

const iterationLabel = (iteration) => {
  if (!iteration) return '';
  const parts = [iteration.name || `Iteration ${iteration.id}`];
  const dates = [iteration.start_date, iteration.end_date]
    .filter(Boolean)
    .map(formatDate)
    .join(' - ');
  if (dates) parts.push(dates);
  return parts.join(' - ');
};

const ProjectInvoicesPanel = ({
  projectId,
  iterations = [],
  className = '',
  maxHeight = '560px',
}) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState(null);

  const iterationOptions = useMemo(
    () =>
      [...(iterations || [])].sort((a, b) => {
        const aDate = a.start_date || a.end_date || '';
        const bDate = b.start_date || b.end_date || '';
        return aDate.localeCompare(bDate) || (a.name || '').localeCompare(b.name || '');
      }),
    [iterations],
  );

  const loadInvoices = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.get(`/billing-batches/cash-events?project_id=${projectId}`);
      const nextInvoices = (Array.isArray(data) ? data : [])
        .filter((invoice) => invoice.direction !== 'outflow' && invoice.status !== 'Annule')
        .sort((a, b) => {
          const aDate = a.issue_date || a.effective_event_date || a.event_date || '';
          const bDate = b.issue_date || b.effective_event_date || b.event_date || '';
          return bDate.localeCompare(aDate) || Number(b.id || 0) - Number(a.id || 0);
        });
      setInvoices(nextInvoices);
    } catch (err) {
      setInvoices([]);
      setError(err.message || 'Erreur chargement factures');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const totals = useMemo(
    () =>
      invoices.reduce(
        (acc, invoice) => {
          const amount = Number(invoice.amount_eur || 0);
          if (invoice.status === 'Prevu') {
            acc.planned += amount;
          } else {
            acc.invoiced += amount;
          }
          acc.total += amount;
          return acc;
        },
        { planned: 0, invoiced: 0, total: 0 },
      ),
    [invoices],
  );

  const patchInvoice = async (invoice, payload) => {
    setSavingId(invoice.id);
    setError(null);
    try {
      const updated = await apiService.patch(`/billing-batches/cash-events/${invoice.id}`, payload);
      setInvoices((prev) =>
        prev.map((item) => (item.id === invoice.id ? { ...item, ...updated } : item)),
      );
    } catch (err) {
      setError(err.message || 'Association impossible');
    } finally {
      setSavingId(null);
    }
  };

  const handleIterationChange = (invoice, value) => {
    const iterationId = value ? Number(value) : null;
    patchInvoice(invoice, {
      issue_project_id: Number(projectId),
      issue_iteration_id: iterationId,
      issue_date_anchor: iterationId ? normalizeAnchor(invoice.issue_date_anchor) : 'fixed',
      issue_date: iterationId ? null : invoice.issue_date || todayIso(),
    });
  };

  const handleAnchorChange = (invoice, value) => {
    if (!invoice.issue_iteration_id) return;
    patchInvoice(invoice, {
      issue_project_id: Number(projectId),
      issue_iteration_id: invoice.issue_iteration_id,
      issue_date_anchor: value,
      issue_date: null,
    });
  };

  return (
    <div className={`rounded-2xl border border-slate-100 bg-white p-4 shadow-sm ${className}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Factures projet</h2>
          <p className="text-sm text-slate-500">
            Factures rattachees directement au projet, a ses lignes ou a ses iterations.
          </p>
        </div>
        <button
          type="button"
          onClick={loadInvoices}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-900 disabled:opacity-60"
          title="Rafraichir"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Actualiser
        </button>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
        <div className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2">
          <div className="text-[10px] font-semibold uppercase text-slate-400">Total</div>
          <div className="mt-1 font-semibold text-slate-900">{formatMoney(totals.total)}</div>
        </div>
        <div className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2">
          <div className="text-[10px] font-semibold uppercase text-amber-600">Prevu</div>
          <div className="mt-1 font-semibold text-amber-800">{formatMoney(totals.planned)}</div>
        </div>
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-2">
          <div className="text-[10px] font-semibold uppercase text-emerald-600">Envoye</div>
          <div className="mt-1 font-semibold text-emerald-800">{formatMoney(totals.invoiced)}</div>
        </div>
      </div>

      {error && (
        <div className="mt-3 rounded-md border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-600">
          {error}
        </div>
      )}

      <div className="mt-3 min-h-[180px] overflow-y-auto pr-1" style={{ maxHeight }}>
        {loading && invoices.length === 0 ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Chargement des factures...
          </div>
        ) : invoices.length > 0 ? (
          <div className="space-y-2.5">
            {invoices.map((invoice) => {
              const title = invoice.invoice_reference || invoice.label || `Facture ${invoice.id}`;
              const anchorLabel = [
                invoice.issue_project_number,
                invoice.issue_project_name,
                invoice.issue_iteration_name,
              ]
                .filter(Boolean)
                .join(' / ');
              const saving = savingId === invoice.id;
              return (
                <div
                  key={invoice.id}
                  className="rounded-md border border-slate-200 bg-white p-3 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex min-w-0 items-center gap-2">
                        <FileText className="h-4 w-4 flex-none text-blue-500" />
                        <div className="truncate text-sm font-semibold text-slate-900" title={title}>
                          {title}
                        </div>
                      </div>
                      <div className="mt-1 truncate text-xs text-slate-500">
                        {[invoice.batch_reference, invoice.batch_name, invoice.client_name]
                          .filter(Boolean)
                          .join(' - ')}
                      </div>
                      {anchorLabel && (
                        <div className="mt-1 truncate text-[11px] text-blue-600" title={anchorLabel}>
                          Rattachement: {anchorLabel}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-slate-900">
                        {formatMoney(invoice.amount_eur)}
                      </div>
                      <span className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium ${statusClass(invoice.status)}`}>
                        {invoiceStatusLabel(invoice.status)}
                      </span>
                    </div>
                  </div>

                  <div className="mt-3 grid grid-cols-1 gap-2">
                    <label className="space-y-1 text-xs">
                      <span className="font-medium text-slate-500">Iteration de facturation (facultative)</span>
                      <select
                        value={invoice.issue_iteration_id || ''}
                        onChange={(event) => handleIterationChange(invoice, event.target.value)}
                        disabled={saving}
                        className="w-full rounded-md border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-60"
                      >
                        <option value="">Sans iteration</option>
                        {iterationOptions.map((iteration) => (
                          <option key={iteration.id} value={iteration.id}>
                            {iterationLabel(iteration)}
                          </option>
                        ))}
                      </select>
                    </label>

                    {invoice.issue_iteration_id && (
                      <div className="grid grid-cols-2 gap-2">
                        <label className="space-y-1 text-xs">
                          <span className="font-medium text-slate-500">Date facture</span>
                          <select
                            value={normalizeAnchor(invoice.issue_date_anchor)}
                            onChange={(event) => handleAnchorChange(invoice, event.target.value)}
                            disabled={saving}
                            className="w-full rounded-md border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-60"
                          >
                            <option value="iteration_end">Fin iteration</option>
                            <option value="iteration_start">Debut iteration</option>
                          </select>
                        </label>
                        <div className="space-y-1 text-xs">
                          <span className="font-medium text-slate-500">Emission</span>
                          <div className="rounded-md border border-slate-100 bg-slate-50 px-2.5 py-2 text-slate-700">
                            {formatDate(invoice.issue_date)}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {saving && (
                    <div className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-400">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Sauvegarde...
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-8 text-center text-sm text-slate-500">
            Aucune facture liee a ce projet.
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectInvoicesPanel;
